import {useEffect} from "react";
import {Box, GlobalStyles} from "@mui/material";
import {LandingHeader} from "./components/LandingHeader";
import {Hero} from "./components/Hero";
import {Metrics} from "./components/Metrics";
import {ProblemStatement} from "./components/ProblemStatement";
import {HowItWorks} from "./components/HowItWorks";
import {Capabilities} from "./components/Capabilities";
import {Integrations} from "./components/Integrations";
import {Specs} from "./components/Specs";
import {Partners} from "./components/Partners";
import {Faq} from "./components/Faq";
import {ContactDemo} from "./components/ContactDemo";
import {LandingFooter} from "./components/LandingFooter";
import {TrustMarquee} from "./components/TrustMarquee";
import {c} from "./landingTokens";
import "./landing.css";


export const LandingPage = () => {

    // Scroll-reveal: fade/slide elements (and their staggered children) in the first time they
    // enter the viewport. One-shot per element. Skipped entirely under reduced-motion, where the
    // CSS leaves everything visible.
    useEffect(() => {
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
        const els = document.querySelectorAll(".openbrain-landing .reveal, .openbrain-landing .reveal-stagger");
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        entry.target.classList.add("is-visible");
                        io.unobserve(entry.target);
                    }
                });
            },
            {threshold: 0.12, rootMargin: "0px 0px -8% 0px"}
        );
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, []);

    return (
            <Box
            className="openbrain-landing"
            sx={{
                bgcolor: c.bg,
                "--accent": "#6258e8",
                color: c.ink,
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                overflowX: "hidden",
            }}
        >
            <GlobalStyles styles={{
                ":root": {"--accent": "#6258e8"},
                "html": {scrollBehavior: "smooth", scrollPaddingTop: "88px"},
                "@keyframes rise": {from: {opacity: 0, transform: "translateY(26px)"}, to: {opacity: 1, transform: "none"}},
                "@keyframes sectionGlide": {from: {transform: "translateY(24px)"}, to: {transform: "translateY(0)"}},
                "@keyframes glowpulse": {"0%,100%": {opacity: 0.42, transform: "scale(1)"}, "50%": {opacity: 0.72, transform: "scale(1.06)"}},
                "@media (prefers-reduced-motion: no-preference)": {
                    ".landing-section-motion": {animation: "rise .85s cubic-bezier(.16,1,.3,1) both"},
                    "@supports (animation-timeline: view())": {
                        ".landing-section-motion": {
                            opacity: 1,
                            animationName: "sectionGlide",
                            animationDuration: "1s",
                            animationTimingFunction: "cubic-bezier(.16,1,.3,1)",
                            animationFillMode: "both",
                            animationTimeline: "view()",
                            animationRange: "entry 0% cover 28%",
                        },
                    },
                    // Scroll-reveal (JS-driven via IntersectionObserver → .is-visible).
                    ".openbrain-landing .reveal": {opacity: 0, transform: "translateY(28px)", transition: "opacity .9s cubic-bezier(.16,1,.3,1), transform .9s cubic-bezier(.16,1,.3,1)"},
                    ".openbrain-landing .reveal.is-visible": {opacity: 1, transform: "none"},
                    // Staggered children: each direct child rises in sequence once the container is seen.
                    ".openbrain-landing .reveal-stagger > *": {opacity: 0, transform: "translateY(24px)", transition: "opacity .8s cubic-bezier(.16,1,.3,1), transform .8s cubic-bezier(.16,1,.3,1)", transitionDelay: "440ms"},
                    ".openbrain-landing .reveal-stagger.is-visible > *": {opacity: 1, transform: "none"},
                    ".openbrain-landing .reveal-stagger.is-visible > *:nth-child(1)": {transitionDelay: "0ms"},
                    ".openbrain-landing .reveal-stagger.is-visible > *:nth-child(2)": {transitionDelay: "90ms"},
                    ".openbrain-landing .reveal-stagger.is-visible > *:nth-child(3)": {transitionDelay: "180ms"},
                    ".openbrain-landing .reveal-stagger.is-visible > *:nth-child(4)": {transitionDelay: "270ms"},
                    ".openbrain-landing .reveal-stagger.is-visible > *:nth-child(5)": {transitionDelay: "360ms"},
                    ".openbrain-landing .reveal-stagger.is-visible > *:nth-child(6)": {transitionDelay: "440ms"},
                },
                "a": {color: "inherit"},
                "a:hover": {color: "var(--accent, #4f46e5)"},
            }} />
            <LandingHeader/>
            <Hero/>
            <TrustMarquee/>
            <ProblemStatement/>
            <Metrics/>
            <HowItWorks/>
            <Capabilities/>
            <Integrations/>
            <Specs/>
            <Partners/>
            <Faq/>
            <ContactDemo/>
            <LandingFooter/>
        </Box>
    )
}
